import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { v4 as uuidv4 } from 'uuid'
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  styled,
  Typography,
} from '@mui/material'
import { Button } from '../../../../components/buttons'
import {
  Close as CloseIcon,
  DeleteRounded as DeleteRoundedIcon,
  Flag as FlagIcon,
} from '@mui/icons-material'
import { colorPalette } from '../../../../style/colorPalette'
import { intl } from '../../../../../i18n'
import { useMilestoneRepository } from '../../../../../services/adaptors/milestoneRepositoryAdaptor'
import { MilestoneDetail } from '../../../../../domain/entity/MilestoneEntity'
import { createDelta } from '../../../../../domain/value-object/ItemDeltaInputVO'
import { FormState, DEFAULT_ICON, DEFAULT_COLOR, ApiError } from '../../hooks'
import { MilestoneFormFields } from './MilestoneFormFields'
import { addGlobalMessage, MessageLevel } from '../../../../../store/messages'
import {
  ErrorCode,
  isPermissionError,
} from '../../../../../handlers/globalErrorHandler'

type Props = {
  open: boolean
  onClose: () => void
  onSaveComplete?: () => void
  initialDate?: string
  milestone?: MilestoneDetail
}

const initialFormState: FormState = {
  name: '',
  milestoneDate: '',
  description: '',
  iconName: DEFAULT_ICON,
  color: DEFAULT_COLOR,
  achieved: false,
}

const saveEditedMilestone = (
  milestone: MilestoneDetail,
  formState: FormState,
  milestoneRepository: ReturnType<typeof useMilestoneRepository>
) => {
  return milestoneRepository.updateBatchDelta({
    added: [],
    edited: [
      {
        uuid: milestone.uuid,
        lockVersion: milestone.lockVersion,
        name: createDelta(milestone.name, formState.name),
        description: createDelta(
          milestone.description,
          formState.description || undefined
        ),
        milestoneDate: createDelta(
          milestone.milestoneDate,
          formState.milestoneDate
        ),
        iconName: createDelta(milestone.iconName, formState.iconName),
        color: createDelta(milestone.color, formState.color),
        achieved: createDelta(milestone.achieved, formState.achieved),
      },
    ],
    deleted: [],
  })
}

const saveNewMilestone = (
  formState: FormState,
  milestoneRepository: ReturnType<typeof useMilestoneRepository>
) => {
  return milestoneRepository.updateBatchDelta({
    added: [
      {
        uuid: uuidv4(),
        name: formState.name,
        milestoneDate: formState.milestoneDate,
        description: formState.description || undefined,
        iconName: formState.iconName,
        color: formState.color,
        achieved: false,
      },
    ],
    edited: [],
    deleted: [],
  })
}

const deleteMilestone = (
  milestone: MilestoneDetail,
  milestoneRepository: ReturnType<typeof useMilestoneRepository>
) => {
  return milestoneRepository.updateBatchDelta({
    added: [],
    edited: [],
    deleted: [{ uuid: milestone.uuid, lockVersion: milestone.lockVersion }],
  })
}

const SingleMilestoneDialog = ({
  open,
  onClose,
  onSaveComplete,
  initialDate,
  milestone,
}: Props) => {
  const [formState, setFormState] = useState<FormState>(initialFormState)
  const [isSaving, setIsSaving] = useState(false)
  const [discardConfirmDialogOpen, setDiscardConfirmDialogOpen] =
    useState(false)
  const [deleteConfirmDialogOpen, setDeleteConfirmDialogOpen] = useState(false)

  const dispatch = useDispatch()
  const milestoneRepository = useMilestoneRepository()

  const isEditMode = !!milestone

  const hasFormChanges = useMemo(() => {
    if (!isEditMode) {
      return (
        formState.name !== '' ||
        formState.milestoneDate !== (initialDate ?? '') ||
        formState.description !== '' ||
        formState.iconName !== DEFAULT_ICON ||
        formState.color !== DEFAULT_COLOR
      )
    }
    return (
      formState.name !== milestone.name ||
      formState.milestoneDate !== milestone.milestoneDate ||
      formState.description !== (milestone.description ?? '') ||
      formState.iconName !== (milestone.iconName ?? DEFAULT_ICON) ||
      formState.color !== (milestone.color ?? DEFAULT_COLOR) ||
      formState.achieved !== milestone.achieved
    )
  }, [formState, isEditMode, milestone, initialDate])

  const handleDialogEntered = useCallback(() => {
    if (milestone) {
      setFormState({
        name: milestone.name,
        milestoneDate: milestone.milestoneDate,
        description: milestone.description ?? '',
        iconName: milestone.iconName ?? DEFAULT_ICON,
        color: milestone.color ?? DEFAULT_COLOR,
        achieved: milestone.achieved,
      })
    } else {
      setFormState({
        ...initialFormState,
        milestoneDate: initialDate ?? '',
      })
    }
  }, [milestone, initialDate])

  const handleFieldChange = useCallback(
    (field: keyof FormState, value: string | boolean) => {
      setFormState(prev => ({ ...prev, [field]: value }))
    },
    []
  )

  const handleSave = useCallback(async () => {
    if (!formState.name || !formState.milestoneDate) return

    setIsSaving(true)
    try {
      if (isEditMode && milestone) {
        await saveEditedMilestone(milestone, formState, milestoneRepository)
      } else {
        await saveNewMilestone(formState, milestoneRepository)
      }
      onSaveComplete?.()
      onClose()
    } catch (error) {
      console.error('Failed to save milestone:', error)
      if (isPermissionError(error)) {
        throw error
      }
      const apiError = error as ApiError
      const isBusinessError = apiError?.code === ErrorCode.BUSINESS_ERROR
      dispatch(
        addGlobalMessage({
          type: isBusinessError ? MessageLevel.WARN : MessageLevel.ERROR,
          title: isBusinessError
            ? apiError.detail
            : intl.formatMessage({ id: 'milestone.error.saveFailed' }),
        })
      )
    } finally {
      setIsSaving(false)
    }
  }, [
    formState,
    onClose,
    onSaveComplete,
    milestoneRepository,
    isEditMode,
    milestone,
    dispatch,
  ])

  const isValid = formState.name && formState.milestoneDate

  const handleCloseDialog = useCallback(() => {
    if (hasFormChanges) {
      setDiscardConfirmDialogOpen(true)
      return
    }
    onClose()
  }, [hasFormChanges, onClose])

  const handleCancelDiscard = useCallback(() => {
    setDiscardConfirmDialogOpen(false)
  }, [])

  const handleConfirmDiscard = useCallback(() => {
    setDiscardConfirmDialogOpen(false)
    onClose()
  }, [onClose])

  const handleRequestDelete = useCallback(() => {
    setDeleteConfirmDialogOpen(true)
  }, [])

  const handleCancelDelete = useCallback(() => {
    setDeleteConfirmDialogOpen(false)
  }, [])

  const handleConfirmDelete = useCallback(async () => {
    if (!milestone) return

    setIsSaving(true)
    try {
      await deleteMilestone(milestone, milestoneRepository)
      dispatch(
        addGlobalMessage({
          type: MessageLevel.SUCCESS,
          title: intl.formatMessage({ id: 'milestone.message.deleteSuccess' }),
        })
      )
      onSaveComplete?.()
      onClose()
    } catch (error) {
      if (isPermissionError(error)) {
        throw error
      }
      const apiError = error as ApiError
      const isBusinessError = apiError?.code === ErrorCode.BUSINESS_ERROR
      dispatch(
        addGlobalMessage({
          type: isBusinessError ? MessageLevel.WARN : MessageLevel.ERROR,
          title: isBusinessError
            ? apiError.detail
            : intl.formatMessage({ id: 'milestone.error.deleteFailed' }),
        })
      )
    } finally {
      setIsSaving(false)
      setDeleteConfirmDialogOpen(false)
    }
  }, [milestone, milestoneRepository, dispatch, onSaveComplete, onClose])

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault()
        e.stopPropagation()
        if (isValid && !isSaving && hasFormChanges) {
          handleSave()
        }
      }
    },
    [isValid, isSaving, hasFormChanges, handleSave]
  )

  useEffect(() => {
    if (!open) return

    window.addEventListener('keydown', handleKeyDown, true)
    return () => {
      window.removeEventListener('keydown', handleKeyDown, true)
    }
  }, [open, handleKeyDown])

  return (
    <>
      <StyledDialog
        open={open}
        onClose={handleCloseDialog}
        maxWidth={false}
        TransitionProps={{ onEnter: handleDialogEntered }}
      >
        <StyledDialogTitle>
          <TitleContent>
            <StyledFlagIcon />
            <Typography variant="h6" component="span">
              {intl.formatMessage({
                id: isEditMode
                  ? 'milestone.dialog.editTitle'
                  : 'milestone.dialog.addTitle',
              })}
            </Typography>
          </TitleContent>
          <IconButton onClick={handleCloseDialog} size="small">
            <CloseIcon />
          </IconButton>
        </StyledDialogTitle>

        <StyledDialogContent>
          <FormContainer>
            <MilestoneFormFields
              formState={formState}
              onFieldChange={handleFieldChange}
              showAchievedToggle={isEditMode}
              autoFocusName={true}
            />
          </FormContainer>
        </StyledDialogContent>

        <StyledDialogActions>
          {isEditMode && (
            <Button
              colorPattern="pink"
              variant="outlined"
              onClick={handleRequestDelete}
              disabled={isSaving}
            >
              <DeleteRoundedIcon sx={{ fontSize: 16 }} />
              {intl.formatMessage({ id: 'milestone.action.delete' })}
            </Button>
          )}
          <SaveCancelActions>
            <Button
              colorPattern="monotone"
              variant="outlined"
              onClick={handleCloseDialog}
            >
              {intl.formatMessage({ id: 'milestone.button.cancel' })}
            </Button>
            <SaveButton
              colorPattern="skyBlue"
              disabled={!isValid || isSaving || !hasFormChanges}
              onClick={handleSave}
            >
              {isSaving && <CircularProgress size={16} color="inherit" />}
              {isSaving
                ? intl.formatMessage({ id: 'milestone.button.saving' })
                : intl.formatMessage({ id: 'milestone.button.save' })}
            </SaveButton>
          </SaveCancelActions>
        </StyledDialogActions>
      </StyledDialog>

      <Dialog
        open={deleteConfirmDialogOpen}
        onClose={handleCancelDelete}
        maxWidth="xs"
      >
        <DialogTitle>
          {intl.formatMessage({ id: 'milestone.confirm.deleteTitle' })}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {intl.formatMessage(
              { id: 'milestone.confirm.deleteMessage' },
              { name: milestone?.name ?? '' }
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            colorPattern="monotone"
            variant="outlined"
            onClick={handleCancelDelete}
            disabled={isSaving}
          >
            {intl.formatMessage({ id: 'milestone.button.cancel' })}
          </Button>
          <Button
            colorPattern="pink"
            onClick={handleConfirmDelete}
            disabled={isSaving}
          >
            {isSaving && <CircularProgress size={16} color="inherit" />}
            {intl.formatMessage({ id: 'milestone.action.delete' })}
          </Button>
        </DialogActions>
      </Dialog>

      <DiscardConfirmDialog
        open={discardConfirmDialogOpen}
        onClose={handleCancelDiscard}
      >
        <DialogTitle>
          {intl.formatMessage({ id: 'milestone.confirm.discardTitle' })}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {intl.formatMessage({ id: 'milestone.confirm.discardMessage' })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            colorPattern="monotone"
            variant="outlined"
            onClick={handleCancelDiscard}
          >
            {intl.formatMessage({ id: 'milestone.confirm.cancel' })}
          </Button>
          <Button colorPattern="pink" onClick={handleConfirmDiscard}>
            {intl.formatMessage({ id: 'milestone.confirm.discardButton' })}
          </Button>
        </DialogActions>
      </DiscardConfirmDialog>
    </>
  )
}

const StyledDialog = styled(Dialog)({
  '& .MuiDialog-paper': {
    width: 620,
    height: 730,
  },
})

const StyledDialogTitle = styled(DialogTitle)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  borderBottom: `1px solid ${colorPalette.monotone[2]}`,
  paddingBottom: 16,
})

const TitleContent = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
})

const StyledFlagIcon = styled(FlagIcon)({
  color: colorPalette.skyBlue[7],
})

const StyledDialogContent = styled(DialogContent)({
  paddingTop: 32,
  overflow: 'auto',
})

const FormContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 20,
  paddingTop: 16,
})

const StyledDialogActions = styled(DialogActions)({
  borderTop: `1px solid ${colorPalette.monotone[2]}`,
  justifyContent: 'space-between',
  paddingLeft: 24,
  paddingRight: 24,
  paddingTop: 16,
  paddingBottom: 16,
})

const SaveCancelActions = styled('div')({
  display: 'flex',
  gap: 8,
  marginLeft: 'auto',
})

const SaveButton = styled(Button)({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
})

const DiscardConfirmDialog = styled(Dialog)`
  & .MuiDialog-paper {
    min-width: 340px;
  }
`

export default SingleMilestoneDialog
